import React, { useState, useRef } from "react";
import { useSelector } from 'react-redux'
import {
  DataTable, Table, TableBody, TableCell, TableContainer,
  TableHead, TableHeader, TableRow, TableToolbar, TableToolbarContent,
  TableToolbarSearch, Button, Tag
} from '@carbon/react';
import { useSorting } from '../../../modules/hooks/sorting';
import ScheduledModalContainer from './ScheduledModal.container';

const EscheduledTable = _ => {
  const button = useRef(null);
  const [open, setOpen] = useState(false);
  const [selectedTruck, setSelectedTruck] = useState('')
  const { data } = useSelector(state => state.scheduled)
  const handleToggleModal = currTruck => () => {
    setSelectedTruck(currTruck)
    setOpen(true)
  }
  const headerData = [
    {
      header: 'N° Orden', // Corresponds to the truckPatent field
      key: 'orderNumber',
    },
    {
      header: 'N° INTERNO EQUIPO', // Corresponds to the truckPatent field
      key: 'patente',
    },
    {
      header: 'CATEGORÍA DE EQUIPO', // Corresponds to the device_id field
      key: 'equipmentCategory',
    },
    {
      header: 'FECHA ENTREGA', // Corresponds to the ciclo_pto field
      key: 'deliveryDate',
    },
    {
      header: 'HORA ENTREGA', // Corresponds to the ciclo_pto field
      key: 'deliveryTime',
    },
    {
      header: 'INCREMENTO HOROMETRO',
      key: 'horometer_diff_number',
    },
    {
      header: 'DELTA DE HRS',
      key: 'horometer_diff'
    },
    {
      header: 'COMENTARIO', // Corresponds to the ciclo_pto field
      key: 'comment',
    }
  ];

  // Define thresholds for different equipment categories
  const categoryThresholds = {
    "BULLDOZER": 350,
    "MOTONIVELADORA": 350,
    "RODILLO": 350,
    "RETROEXCAVADORA": 350,
    "EXCAVADORA": 350,
    "CARGADOR FRONTAL": 350,
    "CAMION TOLVA": 500, // Defaulting to 500 if range is not clearly defined
    "CAMION ALJIBE": 500, // Assuming midpoint for range (500-600)
    "CAMION PLUMA": 500,
    "CAMION COMBUSTIBLE": 500,
    "CAMION ARTICULADO": 500,
    "CAMION DE SERVICIO": 500,
    "TRACTO PLUMA": 500
  };

  const getTagProps = (category, delta) => {
    const threshold = categoryThresholds[category] || 0;
    if (delta === null || isNaN(delta)) {
      return { type: "cool-gray", label: "Revisar Syrus", priority: 3 };
    }
  
    if (delta < threshold) {
      return { type: "green", label: "Anticipado", priority: 2 };
    } else if (delta >= threshold && delta < threshold + 50) {
      return { type: "gray", label: "Óptimo", priority: 1 };
    } else if (delta >= threshold + 50) {
      return { type: "red", label: "Postergado", priority: 0 };
    }
  
    return { type: "cool-gray", label: "Revisar Syrus", priority: 3 };
  };  



  const handleButtonClick = (row) => {
    console.log(row)
    alert(`Clicked on row with Order Number: ${row.id}`);
  };

  const convertToCSV = (data) => {
    // Define the mapping between the table headers and the object keys in `data`
    const headerMapping = {
      orderNumber: 'N° Orden',
      patente: 'N° INTERNO EQUIPO',
      equipmentCategory: 'CATEGORÍA DE EQUIPO',
      deliveryDate: 'FECHA ENTREGA',
      deliveryTime: 'HORA ENTREGA',
      horometer_mant_syrus: 'HOROMETRO ÚLTIMO MANTENIMIENTO SYRUS',
      horometer: 'HOROMETRO ÚLTIMO MANTENIMIENTO ACCIONA',
      horometer_last: 'HOROMETRO ACTUAL',
      comment: 'COMENTARIO'
    };

    if (data.length === 0) {
      return '';
    }

    const headers = Object.values(headerMapping).join(',') + '\n';

    const rows = data.map(row => {
      return Object.keys(headerMapping).map(key => {
        let value = row[key];
        // Check if the value is a number
        if (!isNaN(value) && value.toString().indexOf('.') !== -1) {
          // If it's a number with a dot, replace the dot with a comma
          value = value.toString().replace('.', ',');
        }
        return `"${value}"`; // Apply text formatting to all fields
      }).join(',');
    }).join('\n');

    return headers + rows;
  };

  const downloadCSV = (csvContent, fileName = 'download.csv') => {
    // Create a Blob with the CSV content
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    // Create a hidden link element
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);

    // Append link to the body, click it, and then remove it
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownload = () => {
    const csv = convertToCSV(data);

    downloadCSV(csv, 'Mantenimientos Programados.csv');
  };

  // Filter out rows that don't have a key in categoryThresholds
  const filteredData = data.filter(row =>
    categoryThresholds.hasOwnProperty(row.equipmentCategory?.trim())
  );

const sortedFilteredData = [...filteredData]
  .map(row => {
    const mant = Number(row.horometer_mant_syrus);
    const last = Number(row.horometer_last);
    const diff = !isNaN(mant) && !isNaN(last) ? last - mant : null;
    return {
      ...row,
      horometer_diff: diff
    };
  })
  .sort((a, b) => {
    const categoryA = a.equipmentCategory?.trim() || "";
    const categoryB = b.equipmentCategory?.trim() || "";

    const deltaA = a.horometer_diff !== null ? Number(a.horometer_diff) : null;
    const deltaB = b.horometer_diff !== null ? Number(b.horometer_diff) : null;

    const priorityA = getTagProps(categoryA, deltaA).priority;
    const priorityB = getTagProps(categoryB, deltaB).priority;

    return priorityA - priorityB;
  });

  
  const { rows, headers } = useSorting(sortedFilteredData, headerData);
  

  const Default = () => (
    <DataTable rows={rows} headers={headers} width="100%">
      {({
        rows,
        headers,
        getHeaderProps,
        getRowProps,
        getTableProps,
        getTableContainerProps,
        onInputChange,
        sortBy,
      }) => (
        <TableContainer {...getTableContainerProps()}>
          <TableToolbar>
            <TableToolbarContent>
              <TableToolbarSearch onChange={onInputChange} />
              <Button onClick={handleDownload}>Descargar</Button>
            </TableToolbarContent>
          </TableToolbar>
          <Table {...getTableProps()} aria-label="sample table">
            <TableHead>
              <TableRow>
                {headers.map((header, i) => {
                  const { key: _unusedKey, ...headerProps } = getHeaderProps({
                    header,
                    isSortable:
                      (headerData[i]?.sortDirection ?? "NONE") !== "NONE",
                    onClick: (_, sortState) => sortBy(sortState.sortHeaderKey),
                  });

                  return (
                    <TableHeader
                      key={i}
                      sortDirection={headerData[i]?.sortDirection ?? "NONE"}
                      {...headerProps}
                    >
                      {header.header}
                    </TableHeader>
                  );
                })}
                <TableHeader />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const { key: _unusedKey, ...rowProps } = getRowProps({ row });

                return (
                  <React.Fragment key={row.id}>
                    <TableRow {...rowProps}>
                      {row.cells.map((cell, index) => {
                        if (headers[index]?.key === "horometer_diff") {
                          const categoryCell = row.cells.find(
                            (c) => c.info?.header === "equipmentCategory"
                          );
                          const category = categoryCell?.value?.trim() || "";
                          const truck = row.cells.find(
                            (c) => c.info?.header === "patente"
                          );

                          let delta = cell.value;
                          if (
                            delta === "" ||
                            delta === undefined ||
                            delta === null
                          ) {
                            delta = null;
                          } else {
                            delta = Number(delta);
                          }

                          const { type, label } = getTagProps(category, delta);

                          return (
                            <TableCell key={cell.id}>
                              <Tag type={type}>{label}</Tag>
                            </TableCell>
                          );
                        }

                        return <TableCell key={cell.id}>{cell.value}</TableCell>;
                      })}
                      <TableCell key="action">
                        <Button
                          size="sm"
                          onClick={handleToggleModal(row.cells[1].value)}
                        >
                          ...
                        </Button>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </DataTable>
  );


  return (
    <>
      <Default />
      <ScheduledModalContainer button={button.current} open={open} setOpen={setOpen} truckPatent={selectedTruck} />
    </>

  )
}

export default EscheduledTable;
